import React from 'react';
import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  CustomButton,
} from '../../../common/components';

const ActiveContractsDataTable = ({
  contractsCount,
  plans,
  groups,
  onEditContracts,
  onOpenDialog,
  isLoading,
  LoadingComponent,
}) => {
  const hasContractsWithoutSubscribers = (planUuid, isPremium = false) => {
    if (!contractsCount || !contractsCount.no_subscribers) return false;

    const hasGroupContracts = groups.some((group) => {
      const groupCounts = contractsCount.no_subscribers[group.uuid] || {};
      const relevantCount = isPremium
        ? groupCounts.premium_count || 0
        : groupCounts.regular_count || 0;

      return group.uuid_plan === planUuid && relevantCount > 0;
    });

    const noGroupCounts = contractsCount.no_group?.[planUuid] || {};
    const relevantNoGroupCount = isPremium
      ? noGroupCounts.premium_count || 0
      : noGroupCounts.regular_count || 0;

    const hasNonGroupContracts = relevantNoGroupCount > 0;

    return hasGroupContracts || hasNonGroupContracts;
  };

  const hasContractsWithSubscribers = (planUuid, isPremium = false) => {
    if (!contractsCount || !contractsCount.with_subscribers) return false;

    return groups.some((group) => {
      const groupCounts = contractsCount.with_subscribers[group.uuid] || {};
      const relevantCount = isPremium
        ? groupCounts.premium_count || 0
        : groupCounts.regular_count || 0;

      return group.uuid_plan === planUuid && relevantCount > 0;
    });
  };

  const handleWithoutSubscribersClick = async (planUuid, isPremium) => {
    onOpenDialog();
    const filters = {
      archived: false,
      uuid_plan: planUuid,
      has_subscribers: false,
      is_premium: isPremium,
    };

    await onEditContracts(filters);
  };

  const handleWithSubscribersClick = async (planUuid, isPremium) => {
    onOpenDialog();
    const filters = {
      archived: false,
      uuid_plan: planUuid,
      has_subscribers: true,
      is_premium: isPremium,
    };

    await onEditContracts(filters);
  };

  const renderPlanRow = (plan, isPremium) => {
    const planName = isPremium ? `${plan.name} Premium` : plan.name;

    return (
      <TableRow key={`${plan.uuid}-${isPremium ? 'premium' : 'regular'}`}>
        <RowTableCell>{planName}</RowTableCell>

        <RowTableCell>
          {groups.map((group, key) => {
            if (!contractsCount?.no_subscribers?.[group.uuid]) return null;

            const groupCounts = contractsCount.no_subscribers[group.uuid];
            const numContratos = isPremium
              ? groupCounts.premium_count || 0
              : groupCounts.regular_count || 0;

            if (group.uuid_plan === plan.uuid && numContratos > 0) {
              return (
                <React.Fragment key={key}>
                  <strong>{group.name}:</strong> {numContratos}
                  <br />
                </React.Fragment>
              );
            }
            return null;
          })}

          {(() => {
            if (!contractsCount?.no_group?.[plan.uuid]) return null;

            const noGroupCounts = contractsCount.no_group[plan.uuid];
            const noGroupCount = isPremium
              ? noGroupCounts.premium_count || 0
              : noGroupCounts.regular_count || 0;

            if (noGroupCount > 0) {
              return (
                <React.Fragment>
                  <strong>SIN GRUPO:</strong> {noGroupCount}
                  <br />
                </React.Fragment>
              );
            }
            return null;
          })()}
        </RowTableCell>

        <RowTableCell>
          <CustomButton
            variant="base"
            disabled={!hasContractsWithoutSubscribers(plan.uuid, isPremium)}
            onClick={() => handleWithoutSubscribersClick(plan.uuid, isPremium)}
            buttonIcon={EditIcon}
            sx={{ paddingX: 5 }}
          />
        </RowTableCell>

        <RowTableCell>
          {groups.map((group, key) => {
            if (!contractsCount?.with_subscribers?.[group.uuid]) return null;

            const groupCounts = contractsCount.with_subscribers[group.uuid];
            const numContratos = isPremium
              ? groupCounts.premium_count || 0
              : groupCounts.regular_count || 0;

            if (group.uuid_plan === plan.uuid && numContratos > 0) {
              return (
                <React.Fragment key={key}>
                  <strong>{group.name}</strong>: {numContratos}
                  <br />
                </React.Fragment>
              );
            }
            return null;
          })}
        </RowTableCell>

        <RowTableCell>
          <CustomButton
            variant="base"
            disabled={!hasContractsWithSubscribers(plan.uuid, isPremium)}
            onClick={() => handleWithSubscribersClick(plan.uuid, isPremium)}
            buttonIcon={EditIcon}
            sx={{ paddingX: 5 }}
          />
        </RowTableCell>
      </TableRow>
    );
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <GeneralTableBox showHover={false} hasMaxHeight={false} showZebra={false}>
      <Table aria-label="active contracts table">
        <TableHead>
          <TableRow>
            <HeadTableCell wmd="15%">Nombre del plan</HeadTableCell>
            <HeadTableCell wmd="20%">Contratos sin Suscripción</HeadTableCell>
            <HeadTableCell wmd="5%">Editar</HeadTableCell>
            <HeadTableCell wmd="20%">Contratos con Suscripción</HeadTableCell>
            <HeadTableCell wmd="5%">Editar</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ borderRadius: '15px' }}>
          {plans.flatMap((plan) => [renderPlanRow(plan, false), renderPlanRow(plan, true)])}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default ActiveContractsDataTable;
